import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ModuleIntroModal } from "../../module/ModuleIntroModal";
import { ModuleStudioShell } from "../../module/ModuleStudioShell";
import { GymCountdownOverlay, type GymCountdownPhase } from "../../gym/GymCountdownOverlay";
import { PracticeSurface } from "../../practice/PracticeSurface";
import { ProgressionPlayer } from "../../progressions/ProgressionPlayer";
import { Button } from "../../ui/button";
import { CHORD_HUB_THEME as T } from "../../../lib/chords/chordHubTheme";
import type { ProgressionPlayHubCard } from "../../../lib/chords/chordHubTypes";
import { getVoicingFromChordName } from "../../../lib/chords/chordHubVoicing";
import { useInstrument } from "../../../contexts/InstrumentContext";

const COUNTDOWN_TICK_MS = 880;
const COUNTDOWN_GO_MS = 1_000;

type StudioPhase = "intro" | "countdown" | "play" | "complete";

export interface ChordProgressionStudioProps {
  card: ProgressionPlayHubCard;
  wasCompletedBefore: boolean;
  onBack: () => void;
  onComplete: () => void;
}

export function ChordProgressionStudio({
  card,
  wasCompletedBefore,
  onBack,
  onComplete,
}: ChordProgressionStudioProps) {
  const { surface } = useInstrument();
  const validChords = useMemo(
    () => card.payload.progression.filter((c) => c.trim() !== ""),
    [card.payload.progression]
  );

  const [phase, setPhase] = useState<StudioPhase>("intro");
  const [countdown, setCountdown] = useState<GymCountdownPhase | null>(null);
  const [bpm, setBpm] = useState(card.payload.bpm);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [seenIndices, setSeenIndices] = useState<Set<number>>(() => new Set());
  const [loopCompleted, setLoopCompleted] = useState(false);
  const prevIndexRef = useRef(-1);

  const activeChord = activeIndex >= 0 ? validChords[activeIndex] : validChords[0] ?? null;
  const currentVoicing = useMemo(
    () => (activeChord ? getVoicingFromChordName(activeChord) : null),
    [activeChord]
  );

  const mutedStrings = useMemo(() => currentVoicing?.mutedStrings ?? [], [currentVoicing]);

  const highlightedPositions = useMemo(() => {
    if (!currentVoicing) return [];
    return currentVoicing.positions.filter((p) => !mutedStrings.includes(p.string));
  }, [currentVoicing, mutedStrings]);

  const canComplete =
    validChords.length > 0 &&
    (loopCompleted || seenIndices.size >= validChords.length);

  useEffect(() => {
    if (!isPlaying || activeIndex < 0) return;
    setSeenIndices((prev) => new Set(prev).add(activeIndex));

    const prev = prevIndexRef.current;
    if (
      prev >= 0 &&
      activeIndex === 0 &&
      prev === validChords.length - 1
    ) {
      setLoopCompleted(true);
    }
    prevIndexRef.current = activeIndex;
  }, [activeIndex, isPlaying, validChords.length]);

  const handleBeginCountdown = useCallback(() => {
    setCountdown(3);
  }, []);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === "go") {
      const id = window.setTimeout(() => {
        setPhase("play");
        setCountdown(null);
      }, COUNTDOWN_GO_MS);
      return () => window.clearTimeout(id);
    }
    const id = window.setTimeout(() => {
      if (typeof countdown === "number") {
        if (countdown <= 1) setCountdown("go");
        else setCountdown(countdown - 1);
      }
    }, COUNTDOWN_TICK_MS);
    return () => window.clearTimeout(id);
  }, [countdown]);

  const handleFinish = useCallback(() => {
    setIsPlaying(false);
    setPhase("complete");
    window.setTimeout(() => {
      onComplete();
    }, 900);
  }, [onComplete]);

  const dimNeck = phase === "intro" || countdown !== null;

  if (surface === "keyboard") {
    return (
      <ModuleStudioShell
        title={card.title}
        subtitle={card.subtitle}
        onBack={onBack}
        accent={T.accent}
        border={T.border}
        textMain={T.textMain}
        textMuted={T.textMuted}
        textDim={T.textDim}
      >
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <p className="text-sm max-w-md" style={{ color: T.textMuted }}>
            Esta lección está optimizada para guitarra. Cambia el instrumento de sesión o abre el
            explorador libre para piano.
          </p>
          <Button onClick={onBack} className="mt-4" variant="outline">
            Volver al hub
          </Button>
        </div>
      </ModuleStudioShell>
    );
  }

  return (
    <ModuleStudioShell
      title={card.title}
      subtitle={card.subtitle}
      onBack={onBack}
      accent={T.accent}
      border={T.border}
      textMain={T.textMain}
      textMuted={T.textMuted}
      textDim={T.textDim}
      headerExtra={
        phase === "play" ? (
          <span className="text-[10px] tabular-nums" style={{ color: T.textDim }}>
            {seenIndices.size}/{validChords.length} acordes
          </span>
        ) : undefined
      }
    >
      <div className="flex-1 min-h-0 flex flex-col gap-3 p-2 sm:p-4">
        <div className="flex-1 min-h-[min(50vh,480px)] relative rounded-2xl overflow-hidden flex items-center justify-center">
          <div
            className="relative w-full h-full max-w-[min(96vw,1100px)] mx-auto flex items-center justify-center transition-opacity duration-500 ease-out"
            style={{ opacity: dimNeck ? 0.35 : 1 }}
          >
            {currentVoicing ? (
              <PracticeSurface
                className="h-full w-full"
                highlightedPositions={highlightedPositions}
                mutedStrings={mutedStrings}
                barre={currentVoicing.barre ?? null}
                showNoteNames
                showDegrees
                visualStyle="lutherie"
              />
            ) : (
              <p className="text-sm" style={{ color: T.textMuted }}>
                {activeChord
                  ? `Sin forma clásica para ${activeChord}`
                  : "Progresión vacía"}
              </p>
            )}
          </div>

          {phase === "play" && activeChord && (
            <div className="absolute top-3 left-1/2 z-30 -translate-x-1/2 pointer-events-none">
              <div
                className="px-4 py-2 rounded-xl text-sm font-bold tabular-nums"
                style={{
                  background: "rgba(28,26,25,0.9)",
                  border: `1px solid ${T.borderHover}`,
                  color: T.accent,
                }}
              >
                {activeChord}
              </div>
            </div>
          )}

          {phase === "intro" && (
            <ModuleIntroModal
              title={card.title}
              subtitle={card.subtitle}
              description={card.description}
              instructions={card.instructions}
              tags={card.tags}
              timeEstimateMin={card.timeEstimateMin}
              accent={T.accent}
              accentSoft={T.accentSoft}
              accentGlow={T.accentGlow}
              borderHover={T.borderHover}
              textMain={T.textMain}
              textMuted={T.textMuted}
              textDim={T.textDim}
              wasCompletedBefore={wasCompletedBefore}
              onBegin={handleBeginCountdown}
            />
          )}

          {countdown !== null && <GymCountdownOverlay phase={countdown} />}

          {phase === "complete" && (
            <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
              <div
                className="rounded-2xl px-8 py-6 text-center module-complete-pop"
                style={{
                  background: "rgba(28,26,25,0.94)",
                  border: `1px solid ${T.accent}`,
                  boxShadow: `0 0 40px ${T.accentGlow}`,
                }}
              >
                <p className="text-lg font-bold" style={{ color: T.textMain }}>
                  ¡Lección completada!
                </p>
                <p className="text-xs mt-1" style={{ color: T.textDim }}>
                  Volviendo al hub…
                </p>
              </div>
            </div>
          )}
        </div>

        {phase === "play" && (
          <div className="shrink-0 flex flex-col gap-3 max-w-2xl mx-auto w-full pb-2">
            <ProgressionPlayer
              progression={card.payload.progression}
              keySignature={card.payload.key}
              bpm={bpm}
              onBpmChange={setBpm}
              isPlaying={isPlaying}
              onTogglePlay={setIsPlaying}
              activeIndex={activeIndex}
              onActiveIndexChange={setActiveIndex}
            />
            <div className="flex justify-center">
              <Button
                onClick={handleFinish}
                disabled={!canComplete}
                className="font-semibold text-white border-0 px-5"
                style={{
                  backgroundColor: canComplete ? T.accent : "rgba(255,255,255,0.12)",
                  opacity: canComplete ? 1 : 0.7,
                }}
              >
                {canComplete
                  ? "Completar lección"
                  : isPlaying
                    ? "Escucha un ciclo completo…"
                    : "Pulsa Play para empezar"}
              </Button>
            </div>
          </div>
        )}
      </div>
      <style>{`
        .module-complete-pop { animation: moduleCompletePop 0.5s ease-out both; }
        @keyframes moduleCompletePop {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </ModuleStudioShell>
  );
}
